import React, { useEffect } from 'react';
import { useAppAbility } from '@gsa/afp-shared-ui-utils';
import Breadcrumbs from '../../../widgets/breadcrumbs';
import { useFederalStandards } from '../fvs-provider';
import { useFvsCommentsContext } from '../fvs-comments-provider';
import FvsRequirementsProvider from './requirements-provider';
import RequirementInfo from './components/requirement-info';
import RequirementTabs from './components/requirement-tabs';
import FvsMessages from '../components/fvs-messages';
import RequirementsModalWrapper from './components/requirements-modal';
import useRequirementsParams from './useRequirementsParams';
import { OPERATIONS, SUBJECTS } from '../../../utilities/constants';

const Requirements = () => {
  const { year, fedStandardCode, standardItemNumber } =
    useRequirementsParams();
  const ability = useAppAbility();

  const {
    standardItem,
    setFvsContextData,
    standardItems,
    isSILoading,
    removeFvsMessageById,
    selectedYear,
    clearAllMessages,
  } = useFederalStandards();

  const { getStandardsComments } = useFvsCommentsContext();

  // 1. Sync year from URL to provider (also re-syncs if something resets it)
  useEffect(() => {
    if (year && selectedYear !== year) {
      setFvsContextData('SET_SELECTED_YEAR', year);
    }
  }, [year, selectedYear]);

  // 2. Sync selection options to provider (triggers standardItems fetching)
  useEffect(() => {
    if (year && fedStandardCode) {
      setFvsContextData('SET_SELECTED_OPTIONS', {
        year,
        fedStandardCode,
        standardItemNumber: standardItemNumber || '',
        vehicleGroup: '',
        vehicleType: '',
      });
    }
  }, [year, fedStandardCode, standardItemNumber]);

  // 3. Set selected standard item AFTER standardItems have loaded
  useEffect(() => {
    if (standardItemNumber && standardItems?.length > 0 && !isSILoading) {
      setFvsContextData('SET_SELECTED_STANDARD_ITEM', standardItemNumber);
    }
  }, [standardItemNumber, standardItems, isSILoading]);

  // 4. Load standards comments once standardItem is available.
  // This runs at this level (not inside RequirementsTable) so comments load
  // even when showCommentTab=true causes the Requirements tab panel to not render.
  useEffect(() => {
    if (!standardItem?.id) return;

    const filter = {
      operator: 'AND',
      value: [
        {
          key: 'linkedEntityId',
          operator: 'EQ',
          value: standardItem.id,
        },
      ],
    };

    if (!ability?.can(OPERATIONS.Manage, SUBJECTS.FVSComments)) {
      filter.value.push({
        key: 'status',
        operator: 'NOTIN',
        value: [4],
      });
    }

    getStandardsComments?.({
      variables: {
        filters: filter,
      },
    });
  }, [standardItem?.id]);

  // Clear generated messages on load and before unload
  useEffect(() => {
    clearAllMessages();
    return () => {
      clearAllMessages();
    };
  }, []);

  const breadcrumbsPath = [
    {
      location: `${window.AFP_CONFIG.appURLs?.home}/home`,
      label: 'Home',
    },
    {
      location: '/catalog/vehicle-standards',
      label: 'Federal Vehicle Standards',
    },
  ];

  return (
    <FvsRequirementsProvider>
      <div>
        <div className="grid-row grid-gap" data-testid="fvs-container">
          <div className="tablet:grid-col">
            <Breadcrumbs
              current={`${standardItem?.standardItemNumber} - ${standardItem?.title} - ${standardItem?.year}`}
              path={breadcrumbsPath}
            />
            <FvsMessages onClose={removeFvsMessageById} />
          </div>
        </div>

        <div className="grid-col">
          <RequirementInfo />
          <RequirementTabs />
        </div>
        {standardItem && !isSILoading && <RequirementsModalWrapper />}
      </div>
    </FvsRequirementsProvider>
  );
};

export default Requirements;
